/**
 * network-printer-adapter — Adapter d'impression réseau via socket TCP native.
 *
 * Pourquoi ce module ?
 * L'implémentation existante sendViaNetwork() (printer-adapters.ts) passe par
 * l'Edge Function print-document hébergée en cloud. Cette Edge Function ne peut
 * PAS ouvrir de socket TCP vers le LAN local du commerçant — les imprimantes
 * réseau ne fonctionnent donc pas.
 *
 * Ce module délègue au plugin Capacitor NetworkPrinterPlugin.java qui, lui,
 * tourne dans l'APK Android sur le même réseau Wi-Fi que l'imprimante.
 *
 * ⚠️ PAS d'import statique de '@capacitor/core' (EXTERNAL dans vite.config.ts —
 * incident écran blanc prod 2026-07-09). Détection native + accès plugin via
 * window.Capacitor (pattern maison, cf. NetworkPrinterPlugin.ts).
 *
 * Contrat :
 *   - APK avec plugin NetworkPrinter : socket TCP directe vers host:port
 *   - APK ancien (sans plugin) ou web : fallback sendViaNetwork (Edge Function)
 *
 * Invariants respectés :
 *   4.B.2 — aucun import supabase direct (logging délégué à logging.ts)
 *   4.B.4 — 0 any
 *   4.A.16 — les octets sont produits par buildEscPos* — ce module ne formate pas
 *   4.B.5 — Strangler Fig : ce module s'ajoute en parallèle, pas en remplacement
 */

import { getNetworkPrinter } from '@/plugins/NetworkPrinterPlugin';
import { devLog } from '@/lib/logger';
import { sendViaNetwork } from './printer-adapters';

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

/** Timeout réseau par défaut (ms) — aligné sur PRINT_TIMEOUT_MS de PrinterAdapter. */
const DEFAULT_NETWORK_TIMEOUT_MS = 10_000;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Encode un Uint8Array en chaîne base64 sans dépasser la limite de pile JS.
 *
 * String.fromCharCode(...data) sur un Uint8Array > ~65 000 octets provoque
 * un "Maximum call stack size exceeded". On découpe en chunks de 8 192 octets.
 */
function encodeBase64(data: Uint8Array): string {
  let binary = '';
  const chunkSize = 8_192;
  for (let offset = 0; offset < data.length; offset += chunkSize) {
    binary += String.fromCharCode(...data.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

// ---------------------------------------------------------------------------
// NetworkPrinterAdapter
// ---------------------------------------------------------------------------

/**
 * Adapter d'impression réseau TCP native.
 *
 * Usage dans printer-router.ts :
 *   import { NetworkPrinterAdapter } from './network-printer-adapter';
 *   return NetworkPrinterAdapter.send(ip, port, bytes);
 */
export const NetworkPrinterAdapter = {
  /**
   * Envoie un buffer ESC/POS brut à une imprimante réseau via socket TCP.
   *
   * @param host      Adresse IP de l'imprimante (ex : "192.168.1.100")
   * @param port      Port TCP — défaut 9100 (raw JetDirect)
   * @param data      Buffer ESC/POS brut (Uint8Array) produit par buildEscPos*
   * @param timeoutMs Timeout de connexion en ms (défaut 10 000)
   * @returns         true si l'impression a réussi
   * @throws          Error si plugin natif indisponible ou erreur réseau
   */
  async send(
    host: string,
    port: number,
    data: Uint8Array,
    timeoutMs: number = DEFAULT_NETWORK_TIMEOUT_MS,
  ): Promise<boolean> {
    // Plugin natif requis : APK rebuildé avec NetworkPrinterPlugin.java.
    // Web ou APK ancien → l'appelant doit utiliser le fallback Edge Function
    // (voir printViaNetworkOrCloud ci-dessous).
    const plugin = getNetworkPrinter();
    if (!plugin) {
      throw new Error(
        "Impression réseau directe (TCP) indisponible : plugin NetworkPrinter absent " +
          "(web, ou APK antérieur au module réseau). Utiliser le fallback Edge Function.",
      );
    }

    const dataBase64 = encodeBase64(data);

    // Appel du plugin natif — la socket TCP est ouverte côté Java.
    // En cas d'erreur réseau (timeout, refus, hôte inconnu), le plugin appelle
    // call.reject() ce qui cause un rejet de la Promise avec le message d'erreur.
    await plugin.printRaw({ host, port, dataBase64, timeoutMs });

    return true;
  },
};

/**
 * Route un buffer ESC/POS vers une imprimante réseau selon la capacité du runtime :
 *   - APK avec plugin NetworkPrinter → socket TCP directe (LAN local)
 *   - web / PWA / APK ancien        → sendViaNetwork (Edge Function print-document)
 *
 * L'Edge Function cloud ne peut pas atteindre les IPs privées (192.168.x.x) du
 * réseau du commerçant ; l'APK rebuildé, sur le même Wi-Fi, ouvre la socket sans relais.
 */
export async function printViaNetworkOrCloud(
  ip: string,
  port: number,
  data: Uint8Array,
): Promise<boolean> {
  if (getNetworkPrinter()) {
    devLog('[PrintService] Network → socket TCP native (NetworkPrinterAdapter)');
    return NetworkPrinterAdapter.send(ip, port, data);
  }
  devLog('[PrintService] Network → Edge Function print-document (fallback web/APK ancien)');
  return sendViaNetwork(ip, port, data);
}
